import { AxiosResponse } from 'axios';
import { call, takeLatest, put, select, all } from 'redux-saga/effects';

import { IVisit } from 'shared/interfaces/visit.interface';
import { IPaymentMethod } from 'shared/interfaces/payment-method.interface';
import { IState } from 'shared/interfaces/state.interface';
import { INote } from 'shared/interfaces/note.interface';
import {
  TAction,
  TGetOneEntityAction,
  TVisitStatus,
  TNoteCreateAction,
  TVisitsResponse
} from 'shared/types';
import VisitService from 'shared/services/VisitsService';
import BillingsService from 'shared/services/BillingsService';
import NoteService from 'shared/services/NoteService';

import {
  getVisitsData,
  setVisitsData,
  getVisitsFail,
  getVisit,
  setVisit,
  createConsultationNote,
  addNewNote,
  deleteConsultationNote,
  removeNote,
  getVisitsByPatient
} from './visits.redux';
import { openNotificationMessage } from 'shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';

function* getVisits({ payload }: TAction<TVisitStatus>) {
  try {
    const result: AxiosResponse<TVisitsResponse> = yield call(
      VisitService.getAllVisits,
      payload
    );

    yield put(setVisitsData(result));
  } catch (error) {
    console.log('error', error);
    yield put(getVisitsFail());
  }
}

function* getAllVisitsByPatient({ payload }: TAction<TVisitStatus>) {
  try {
    const result: AxiosResponse<TVisitsResponse> = yield call(
      VisitService.getVisitsByPatient,
      payload
    );

    yield put(setVisitsData(result));
  } catch (error) {
    console.log('error', error);
    yield put(getVisitsFail());
  }
}

function* getOneVisit({ payload }: TAction<TGetOneEntityAction>) {
  try {
    const [visit, notes]: [IVisit, INote[]] = yield all([
      call(VisitService.getOneVisit, payload.id),
      call(NoteService.getVisitNotes, payload.id)
    ]);
    let paymentMethod: IPaymentMethod | null = null;
    const prescriptionNotes: INote[] = visit.prescription.notes;
    if (
      payload.needPaymentMethod &&
      visit.prescription?.prescriptionFulfillments.length &&
      visit.prescription?.prescriptionFulfillments[
        visit.prescription.prescriptionFulfillments.length - 1
      ].order.paymentIntent?.paymentMethod
    ) {
      paymentMethod = yield call(BillingsService.getPaymentMethodData, {
        id:
          visit.prescription.prescriptionFulfillments[
            visit.prescription.prescriptionFulfillments.length - 1
          ].order.paymentIntent?.paymentMethod || '',
        patient: visit.prescription.patient.id
      });
    }

    yield put(
      setVisit({
        data: visit,
        paymentMethod: paymentMethod,
        notes: [...notes, ...prescriptionNotes]
      })
    );
  } catch (error) {
    console.log('error', error);
    yield put(getVisitsFail());
  }
}

function* createNote({ payload }: TAction<TNoteCreateAction>) {
  try {
    const currentVisit: IVisit = yield select(
      (s: IState) => s.visits.currentVisit.data
    );

    const result: INote = yield call(NoteService.createNote, {
      ...payload,
      relatedEntity: currentVisit.id
    });

    yield put(addNewNote(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully added.'
    });
  } catch (error) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'Note creation was unsuccesful.'
    });
  }
}

function* deleteNote({ payload }: TAction<string>) {
  try {
    yield call(NoteService.deleteNote, payload);
    yield put(removeNote(payload));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully deleted.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Unable to delete note.'
    });
  }
}

function* Visits() {
  yield takeLatest(getVisitsData.type, getVisits);
  yield takeLatest(getVisit.type, getOneVisit);
  yield takeLatest(getVisitsByPatient.type, getAllVisitsByPatient);
  yield takeLatest(createConsultationNote.type, createNote);
  yield takeLatest(deleteConsultationNote.type, deleteNote);
}

export default Visits;
